<template>
    <component
        :is="tag"
        class="sim-cart-item-card"
    >
        <slot v-if="$slots.default" />

        <template v-else-if="cartItem">
            <div class="sim-cart-item-card__img-box">
                <CoreImg
                    :src="cartItem?.imageUrl"
                    :width="140"
                    :alt="cartItem?.name"
                    loading="lazy"
                    fit="contain"
                    class="sim-cart-item-card__img"
                />
            </div>
            <div class="sim-cart-item-card__text-box" :class="$slots.actions ? 'justify-between' : 'justify-center'">
                <div class="sim-cart-item-card__name-box">
                    <NuxtLink :to="cartItem?.productUrls!" class="sim-font-bold">
                        {{ cartItem?.name }}
                    </NuxtLink>

                    <UiLineSeparatedList
                        v-if="cartItem?.hasVariationProperties()"
                        :entries="cartItem.getValuesOfVariationProperties()"
                    />

                    <div class="sim-font-bold">
                        {{ getFormattedPrice(cartItem?.taxedUnitPrice) }}
                    </div>
                </div>
                <slot v-if="$slots.actions" name="actions" />
            </div>
        </template>
    </component>
</template>

<script lang="ts" setup>
import { getFormattedPrice } from '#imports'

const {
    tag = 'div',
    cartItem = null,
} = defineProps<{
    tag?: 'div' | 'li' | 'aside'
    cartItem?: InstanceType<typeof CartItemModel>
}>()

</script>

<style lang="scss" scoped>

.sim-cart-item-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    width: 100%;
    color: $sim-c-primary-800;

    @include sim-text-normal-md;
    @include sim-font-regular;
}

.sim-cart-item-card--cart-item {
    display: flex;
    gap: 1rem;
    justify-content: flex-start;

    @include more-than(lg) {
        display: grid;
        grid-template-columns: 45% 1fr;
        align-items: center;
    };
}

.sim-cart-item-card__img-box {
    flex: 1;
    height: 7rem;
    border-radius: $sim-border-radius-xs;
    overflow: hidden;

    @include more-than(sm) {
        padding: 0 1.25rem;
    };
}

.sim-cart-item-card__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.sim-cart-item-card__text-box {
    flex: 2;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    height: 100%;
}

.sim-cart-item-card__name-box {
    display: flex;
    flex-direction: column;
    align-items: end;
    gap: 0.25rem;

    @include more-than(sm) {
        align-items: start;
    };
}

.sim-cart-item-card__divider {
    position: relative;
    padding-right: 1rem;

    &:not(:last-child)::after {
        position: absolute;
        content: "";
        right: 0.5rem;
        top: 0.2rem;
        height: 0.8125rem;
        width: 0.0625rem;
        background-color: $sim-c-basic-600;
    }
}

</style>
